import React, { useState, useEffect } from 'react';
import i18n from 'utils/i18n';
import { extractDateOrTime } from 'utils/functions';
import Title from 'components/atoms/title';
import AnchorLink from 'components/atoms/anchor-link';
import ExpandedContent from 'components/molecules/expanded-content';
import { useServiceAppointment } from 'providers/service-appointment';
import { IconArrowDown, IconArrowLeft, IconErrorCircle, IconSpinner } from 'components/icons';
import Button from 'components/atoms/button';
import css from './styles.module.scss';

function ServiceAppointmentHistory(props) {
  const t = i18n.useTranslations('components.my-shifts');
  const { state, dispatch } = useServiceAppointment();

  const [loading, setLoading] = useState(true);
  const [displayedItems, setDisplayedItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 25;
  const itemsPerLoad = 4;
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [totalItems, setTotalItems] = useState(0);

  const { className = "", ...other } = props;

  const fetchData = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/appointment/getHistoryByChassi?chassi=${state.selectedVehicleHistory}&page=${page}&pageSize=${pageSize}`);

      if (response.status === 404) {
        setError('not_found');
        setDisplayedItems([]);
      } else if (!response.ok) {
        throw new Error('Failed to fetch data');
      } else {
        const data = await response.json();
        const items = data.content;
        setTotalItems(data.totalItems);

        if (items.length > 0) {
          setAllItems(prevItems => [...prevItems, ...items].slice(0, pageSize));
          setDisplayedItems(prev => [...prev, ...items.slice(0, itemsPerLoad)]);
        }
      }
    } catch (error) {
      setError('generic');
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData(currentPage);
  }, []);

  const seeMoreClick = async () => {
    const nextItems = allItems.slice(displayedItems.length, displayedItems.length + itemsPerLoad);

    if (nextItems.length > 0) {
      setDisplayedItems(prev => [...prev, ...nextItems]);
    }
    if (allItems.length < totalItems) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      await fetchData(nextPage);
    } else {
      setHasMore(false);
    }
  };

  const onBackClick = () => {
    dispatch({ type: 'SET_SELECTED_VEHICLE_HISTORY', payload: null });
    dispatch({ type: 'SET_INITIAL_PAGE_HISTORY', payload: true });
  };

  return (
    <div className={`${css["molecule__service-appointment-history-container"]} ${className}`} {...other}>
      {loading ? (
        <>
          <Title className={css['my-shifts__title']} variant="2">
            {t('record_title')}
          </Title>
          <div className={css['loader']}>
            <div className={css['loader-container']}>
              <div className={css['loader-spinner']}>
                <IconSpinner className={css['icon-spinner']} />
              </div>
              <div className={css['loader-content']} data-testid="loader-content">
                <span data-testid="loader-text">{t('loader_text')}</span>
                <span data-testid="additional-content">{t('content_loading')}</span>
              </div>
            </div>
          </div>
        </>
      ) : error ? (
        error === 'not_found' ? (
          <>
            <button className={css['button_back_shift']} onClick={onBackClick}>
              <IconArrowLeft /> {t('back_shift')}
            </button>
            <Title className={css['my-shifts__title']} variant="2">
              {t('record_title')}
            </Title>
            <p className={css['no-info']}>{t('no_info')}</p>
            <div className={css['miss-data']}>
              <p>{t('history_miss_data')}</p>
              <AnchorLink
                className={css['anchor-link']}
                link="/solicitar-contacto"
                hasArrow={false}
                linkLabel={t('history_report')}
              />
            </div>
          </>
        ) : (
          <div className={css['error']}>
            <button className={css['button_back_shift']} onClick={onBackClick}>
              <IconArrowLeft /> {t('back_shift')}
            </button>
            <div className={css['error-message']}>
              <IconErrorCircle />
              <div>
                <p className={css['error-paragraph']}>{t('error_message_1')}</p>
                <p className={css['error-paragraph']}>{t('error_message_2')}</p>
              </div>
              <Button color='ghost-2' onClick={onBackClick}>
                {t('back_shifts')}
              </Button>
            </div>
          </div>
        )
      ) : (
        <>
          <button className={css['button_back_shift']} onClick={onBackClick}>
            <IconArrowLeft /> {t('back_shift')}
          </button>
          <Title className={css['my-shifts__title']} variant="2">
            {t('record_title')}
          </Title>
          {displayedItems.length > 0 ? (
            <>
            <div className={css['list']}>
              {displayedItems.map((item, index) => (
                <div className={css['appointments']} key={index}>
                  <ExpandedContent className={css['expanded-content']}
                    label={
                      <div className={css['list-item']}>
                        <p className={css['title-appointments']}>{t('history_title_card')}</p>
                        {(item.vehicle || item.vin) && (
                          <div className={css['appointments-body']}>
                            {item.vehicle && (
                              <div>
                                <p className={css['title-p']}>{t('vehicle')}</p>
                                <p>{item.vehicle}</p>
                              </div>
                            )}
                            {item.vin && (
                              <div>
                                <p className={css['title-p']}>{t('chassis')}</p>
                                <p>{item.vin}</p>
                              </div>
                            )}
                          </div>
                        )}
                        <div className={css['appointments-body']}>
                          {item.dealer && (
                            <div>
                              <p className={css['title-p']}>{t('concessionaire')}</p>
                              <p>{item.dealer}</p>
                            </div>
                          )}
                          {item.deliveryDate && (
                            <div>
                              <p className={css['title-p']}>{t('date_done')}</p>
                              <p>{extractDateOrTime(item.deliveryDate, 'date')}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    }
                  >
                    <div className={css['details-record']}>
                      <div className={css['appointments-body']}>
                        <div>
                          <p className={css['title-p']}>{t('service_advisor')}</p>
                          <p>{item.serviceAdvisor}</p>
                        </div>
                        <div>
                          <p className={css['title-p']}>{t('service')}</p>
                          <p>{item.maintenance}</p>
                        </div>
                      </div>
                      <div className={css['appointments-body']}>
                        <div>
                          <p className={css['title-p']}>{t('open_date')}</p>
                          <p>{extractDateOrTime(item.openDate, 'date')}</p>
                        </div>
                        <div>
                          <p className={css['title-p']}>{t('end_date')}</p>
                          <p>{extractDateOrTime(item.endDate, 'date')}</p>
                        </div>
                      </div>
                      {item.description && (
                        <div className={css['appointments-body']}>
                          <div>
                            <p className={css['title-p']}>{t('description')}</p>
                            <p>{item.description}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </ExpandedContent>
                </div>
              ))}
              </div>
              {hasMore && (
                <div className={css['see-more']}>
                  <Button color='ghost-2' onClick={seeMoreClick}>
                    {t('see_more')}
                    <IconArrowDown />
                  </Button>
                </div>
              )}
            </>
          ) : (
            <p className={css['no-info']}>{t('no_info')}</p>
          )}
          <div className={css['miss-data']}>
            <p>{t('history_miss_data')}</p>
            <AnchorLink
              className={css['anchor-link']}
              link="/solicitar-contacto"
              hasArrow={false}
              linkLabel={t('history_report')}
            />
          </div>
        </>
      )}
    </div>
  );
}

export default ServiceAppointmentHistory;
